import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  createLaundryApi,
  deleteLaundryApi,
  exportWashingHistoryByDay,
  exportWashingHistoryByRangeDayApi,
  getWashingHistoryByDay,
  getWashingHistoryByRangeDayApi,
} from '@/apis/washingHistory'
import { apiHost } from '@/configs/api'
import useNotification from '@/hooks/useNotification'
import { GetWashingCountByDayRequest } from '@/types/apis/washingCount'
import { ExportWashingHistoryByDayRequest } from '@/types/apis/washingHistory/ExportWashingHistoryByDayRequest'
import { ExportWashingHistoryByRangeDayRequest } from '@/types/apis/washingHistory/ExportWashingHistoryByRangeDayRequest'
import { GetWashingHistoryByDayResponse } from '@/types/apis/washingHistory/GetWashingHistoryByDayResponse'
import { GetWashingHistoryByRangeDayRequest } from '@/types/apis/washingHistory/GetWashingHistoryByRangeDayRequest'
import { GetWashingHistoryByRangeDayResponse } from '@/types/apis/washingHistory/GetWashingHistoryByRangeDayResponse'
import { LaundryRequest } from '@/types/apis/washingHistory/LaundryRequest'

export const useWashingHistoryByDay = () => {
  const { t } = useTranslation()
  const openNotification = useNotification()
  const [washingHistoryResponse, setWashingHistoryResponse] = useState<GetWashingHistoryByDayResponse>()
  const [washingHistoryByDayResponseLoading, setWashingHistoryByDayResponseLoading] = useState(false)
  const [washingHistoryByRangeDayResponseLoading, setWashingHistoryByRangeDayResponseLoading] = useState(false)
  const [exportWashingHistoryLoading, setExportWashingHistoryLoading] = useState(false)
  const [responseWashingHistoryByRangeDay, setResponseWashingHistoryByRangeDay] =
    useState<GetWashingHistoryByRangeDayResponse>()
  const [laundryLoading, setLaundryLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const get = useCallback(async (request: GetWashingCountByDayRequest) => {
    try {
      setWashingHistoryByDayResponseLoading(true)
      const res = await getWashingHistoryByDay(request)
      setWashingHistoryResponse(res)
    } finally {
      setWashingHistoryByDayResponseLoading(false)
    }
  }, [])

  const getWashingHistoryByRangeDay = useCallback(async (request: GetWashingHistoryByRangeDayRequest) => {
    try {
      setWashingHistoryByRangeDayResponseLoading(true)
      const response = await getWashingHistoryByRangeDayApi(request)
      setResponseWashingHistoryByRangeDay(response)
    } catch {
      // do nothing
    } finally {
      setWashingHistoryByRangeDayResponseLoading(false)
    }
  }, [])

  const exportExcel = async (request: ExportWashingHistoryByDayRequest) => {
    try {
      setExportWashingHistoryLoading(true)
      const result = await exportWashingHistoryByDay(request)
      window.open(`${apiHost}${result.url}`, '_blank')
    } catch (err) {
    } finally {
      setExportWashingHistoryLoading(false)
    }
  }

  const exportExcelWashingHistoryByRangeDay = async (request: ExportWashingHistoryByRangeDayRequest) => {
    try {
      setExportWashingHistoryLoading(true)
      const result = await exportWashingHistoryByRangeDayApi(request)
      window.open(`${apiHost}${result.url}`, '_blank')
    } catch (err) {
    } finally {
      setExportWashingHistoryLoading(false)
    }
  }

  const createLaundry = useCallback(
    async (request: LaundryRequest) => {
      try {
        setLaundryLoading(true)
        await createLaundryApi(request)
        openNotification({ type: 'success', description: t('components.pages.admins.common.create.success') })
      } catch (error) {
        openNotification({ type: 'error', description: t('components.pages.admins.common.create.fail') })
      } finally {
        setLaundryLoading(false)
      }
    },
    [openNotification, t]
  )

  const deleteLaundry = useCallback(
    async (id: number) => {
      try {
        setDeleteLoading(true)
        await deleteLaundryApi(id)
        openNotification({ type: 'success', description: t('components.pages.admins.common.delete.success') })
      } catch (error) {
        openNotification({ type: 'error', description: t('components.pages.admins.common.delete.fail') })
      } finally {
        setDeleteLoading(false)
      }
    },
    [openNotification, t]
  )

  return {
    getWashingHistoryByDay: get,
    getWashingHistoryByRangeDay,
    washingHistoryByRangeDayResponseLoading,
    responseWashingHistoryByRangeDay,
    washingHistoryResponse,
    washingHistoryByDayResponseLoading,
    exportExcelWashingHistoryByDay: exportExcel,
    exportWashingHistoryLoading,
    exportExcelWashingHistoryByRangeDay,
    createLaundry,
    laundryLoading,
    deleteLaundry,
    deleteLoading,
  }
}
